import { type RefCallback, useCallback, useEffect, useRef } from 'react';

type UseWaypointArgs = {
  callback: () => void;
  rootMargin?: number;
  root?: Element | null;
};

type UseWaypointReturn = {
  ref: RefCallback<HTMLElement>;
};

/**
 * 無限スクロールを実現するためのカスタムhook
 * - IntersectionObserverを使用して、指定した要素がビューポートに入ったときにコールバック関数を実行する
 *
 * @param callback - 監視対象がビューポートに入ったときに実行されるコールバック関数
 * @param rootMargin - 監視対象要素に指定するmargin（px）
 * @param root - IntersectionObserver の root 要素（内部スクロールコンテナを指定する場合に使用）
 * @returns ref - 監視対象要素にアタッチする RefCallback
 */
export const useWaypoint = ({
  callback,
  rootMargin = 0,
  root,
}: UseWaypointArgs): UseWaypointReturn => {
  const callbackRef = useRef(callback);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const nodeRef = useRef<HTMLElement | null>(null);

  // 最新の callback を常に参照できるようにする（useEffect の deps から外すため）
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // root / rootMargin が変わるたびに observer を再生成する
  useEffect(() => {
    observerRef.current?.disconnect();

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) callbackRef.current();
      },
      { root: root ?? null, rootMargin: `${rootMargin}px` }
    );

    if (nodeRef.current) {
      observerRef.current.observe(nodeRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [root, rootMargin]);

  const ref: RefCallback<HTMLElement> = useCallback(node => {
    if (nodeRef.current) {
      observerRef.current?.unobserve(nodeRef.current);
    }
    nodeRef.current = node;
    if (node) {
      observerRef.current?.observe(node);
    }
  }, []);

  return { ref };
};
